import { apiAxios, authAxios } from 'utils/axios';
import { withSessionRefresh } from 'api/SessionAuth';
import type { SignInMethodsData } from 'api/SignInMethods';

export interface GoogleAuthResponse {
  status: string;
  message: string;
  api_key: string;
  user_id: number;
  is_new_user: boolean;
}

export interface AccountDetailsResponse {
  status: string;
  data: {
    user_data: {
      user_id: number;
      email: string;
      username: string;
      reg_date: string;
    };
    google_data: {
      google_id: string;
      email: string;
      email_verified: boolean;
      picture: string;
      given_name: string;
      family_name: string;
      locale: string;
    } | null;
    telegram_data: any;
    // Returned by the API since api/controllers/client/get_account_details.py:58.
    // Optional on purpose: an older/failed response must read as "not an admin",
    // never as "unknown, assume yes".
    is_admin?: boolean;
    // Optional on purpose: an API that predates it must read as "unknown"
    // (an error line), never as "nothing is linked".
    sign_in_methods?: SignInMethodsData;
  };
}

/**
 * Google authorization
 * @param token - Google ID token
 * @returns Promise with authorization data
 */
export const googleLogin = async (token: string): Promise<GoogleAuthResponse> => {
  try {
    // console.log('🔄 Authorizing via Google API...');
    const response = await authAxios.post('/auth/google/login', {
      token
    });
    return response.data;
  } catch (error) {
    // console.error('Google login error:', error);
    throw error;
  }
};

/**
 * Attach a Google identity to the signed-in account. The session cookie is the
 * credential; `expectedUserId` is the account the page rendered, so a page and
 * a session that have drifted apart write nothing (409 `account_mismatch`).
 * @param token - Google ID token
 * @param expectedUserId - The user id the page rendered
 */
export const linkGoogleAccount = async (token: string, expectedUserId: number): Promise<void> => {
  await withSessionRefresh(() =>
    authAxios.post('/auth/google/link', {
      token,
      expected_user_id: expectedUserId,
    })
  );
};

/**
 * Get account data (including Google data)
 * @param apiKey - User API key
 * @returns Promise with account data
 */
export const getAccountDetails = async (apiKey: string): Promise<AccountDetailsResponse> => {
  try {
    const response = await apiAxios.get('/get_account_details', {
      headers: {
        'Authorization': `Bearer ${apiKey}`
      }
    });
    return response.data;
  } catch (error) {
    // console.error('Get account details error:', error);
    throw error;
  }
};
